import { useCallback } from 'react';
import toolkitService from '../../services/ToolkitService';

// Ask the project's SyncConnect something — a catalogue READ (`ask`) or a fixed WRITE (`write`) — and wait for the
// answer (toolkit onprem.js). Shared by every on-premise Toolkit screen.
//
// SyncConnect picks a request up when it next checks in, so an answer takes seconds to minutes. `waitFor` looks again
// every few seconds until the request has RETURNED or ERRORed, or `timeoutMs` has passed — then it resolves with the
// request as it stands (still QUEUED / DELIVERED), and the screen says so rather than pretending it failed.
//
// rowsOf(result) turns SyncConnect's col1…colN rows into objects keyed by lower-case column name.

const PENDING = new Set(['QUEUED', 'DELIVERED']);

// NO SYNCCONNECT. When the project has none, `ask` and `write` hand the request to the "run it yourself" dialog
// (ToolkitManualRun, mounted by every screen's ToolkitProjectHeader): it gives the user the script, takes the result
// file they upload, and resolves with the same request a SyncConnect answer would have filled — so no screen changes.
// The project's SyncConnect state is asked once per project (onprem status) and remembered for the session.
let manualHandler = null;
export const setManualHandler = (fn) => { manualHandler = fn; return () => { if (manualHandler === fn) manualHandler = null; }; };
const statusCache = new Map();
export const syncConnectStatus = (ctx) => {
    if (!statusCache.has(ctx.syncId)) {
        statusCache.set(ctx.syncId, toolkitService.onprem(ctx, 'status').then(({ data }) => data)
            .catch((e) => { statusCache.delete(ctx.syncId); throw e; }));
    }
    return statusCache.get(ctx.syncId);
};
const byHand = async (ctx, request) => {
    const st = await syncConnectStatus(ctx);
    if (st.syncConnect) return null;
    if (!manualHandler) throw new Error(st.message);
    return manualHandler(request);
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const rowsOf = (result) => {
    const names = (result?.columns || []).map((c) => String(c).toLowerCase());
    return (result?.rows || []).map((r) => Object.fromEntries(names.map((n, i) => [n, r[`col${i + 1}`]])));
};

export const isPending = (detail) => !!detail && PENDING.has(detail.status);

const useOnPrem = (ctx) => {
    const get = useCallback(async (id, ids) => (await toolkitService.onprem(ctx, 'get', { id, ids })).data, [ctx]);

    const waitFor = useCallback(async (id, { ids, timeoutMs = 15 * 60 * 1000, everyMs = 5000, onUpdate } = {}) => {
        const until = Date.now() + timeoutMs;
        let detail = await get(id, ids);
        while (PENDING.has(detail.status) && Date.now() < until) {
            if (onUpdate) onUpdate(detail);
            await sleep(everyMs);
            detail = await get(id, ids);
        }
        if (onUpdate) onUpdate(detail);
        return detail;
    }, [get]);

    // A catalogue read; resolves with the finished request (detail.result holds the rows).
    const ask = useCallback(async (key, params, opts) => {
        const manual = await byHand(ctx, { kind: 'read', key, params });
        if (manual) return manual;
        const { data } = await toolkitService.onprem(ctx, 'submit', { key, params });
        return waitFor(data.id, opts);
    }, [ctx, waitFor]);

    // A write; `confirm` is the project name the user typed. Resolves with the finished request — detail.writeOutcome
    // holds what the read-back shows.
    const write = useCallback(async (writeOp, args, confirm, opts = {}) => {
        const manual = await byHand(ctx, { kind: 'write', write: writeOp, args, confirm, ids: opts.ids });
        if (manual) return manual;
        const { data } = await toolkitService.onprem(ctx, 'write', { write: writeOp, args, confirm });
        return waitFor(data.id, { ...opts, ids: data.ids || opts.ids });
    }, [ctx, waitFor]);

    return { ask, write, get, waitFor };
};

export default useOnPrem;
